/**
 * An API inbox is a generic pipe: Chatwoot cannot tell what really sits behind
 * it. Marking the inbox with the channel it stands for lets the interface show
 * what suits that channel — group details for WhatsApp, for instance.
 *
 * Stored on the channel as `additional_attributes.api_channel_type`.
 */
export const API_CHANNEL_TYPES = Object.freeze({
  WHATSAPP: 'whatsapp',
  EMAIL: 'email',
  FACEBOOK: 'facebook',
  TELEGRAM: 'telegram',
  INSTAGRAM: 'instagram',
});

/**
 * The real channel each declared type stands for, so an API inbox can borrow
 * what the interface already knows about that channel — its icon, for one.
 */
export const API_CHANNEL_TYPE_TO_CHANNEL = Object.freeze({
  [API_CHANNEL_TYPES.WHATSAPP]: 'Channel::Whatsapp',
  [API_CHANNEL_TYPES.EMAIL]: 'Channel::Email',
  [API_CHANNEL_TYPES.FACEBOOK]: 'Channel::FacebookPage',
  [API_CHANNEL_TYPES.TELEGRAM]: 'Channel::Telegram',
  [API_CHANNEL_TYPES.INSTAGRAM]: 'Channel::Instagram',
});

export const API_CHANNEL_TYPE_OPTIONS = Object.freeze([
  { value: API_CHANNEL_TYPES.WHATSAPP, label: 'WhatsApp' },
  { value: API_CHANNEL_TYPES.EMAIL, label: 'Email' },
  { value: API_CHANNEL_TYPES.FACEBOOK, label: 'Facebook' },
  { value: API_CHANNEL_TYPES.TELEGRAM, label: 'Telegram' },
  { value: API_CHANNEL_TYPES.INSTAGRAM, label: 'Instagram' },
]);

/**
 * Whether the inbox stands for WhatsApp — either a native WhatsApp channel or
 * an API inbox declared as one. Only these take part in the WhatsApp-style
 * conversation list.
 */
export const isWhatsappBackedInbox = inbox =>
  inbox?.channel_type ===
    API_CHANNEL_TYPE_TO_CHANNEL[API_CHANNEL_TYPES.WHATSAPP] ||
  inbox?.additional_attributes?.api_channel_type === API_CHANNEL_TYPES.WHATSAPP;

/**
 * Whether the uazapi instance behind an API inbox stopped delivering: the
 * sync job stamps `additional_attributes.uazapi_status` with what the
 * gateway reports. Returns 'removed', 'disconnected' or '' when all is well.
 */
export const uazapiIssue = inbox => {
  const status = inbox?.additional_attributes?.uazapi_status;
  if (!status || status === 'connected') return '';
  return status === 'removed' ? 'removed' : 'disconnected';
};
